import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { BadgeInput } from '@neo/blocks/input-with-badge'
import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Input } from '@neo/primitives/input'
import { Tooltip } from '@neo/primitives/tooltip'
import { get } from 'lodash'
import { CirclePlus, GitBranch, Trash2 } from 'lucide-react'
import { ToggleGroup } from 'v2/shared/components/toggle-group'
import z from 'zod'
import { NodevarList } from '../../components/nodevar-list'
import { defaultConditions } from '../../config/nodes.config'
import { expressionSchema, v2validations } from '../../config/validation.config'
import type { BranchV2NodeData, SidebarComponentProps } from '../../types'
import { GenericSidebarComponent } from '../sidebar.base'
import { COMPARISON_OPERATORS, getOperatorInfo, listRhsSchema } from './utils'

type FormValues = BranchV2NodeData['config']

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).branch_v2
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
    },
    {
      name: 'conditions',
      type: 'array',
      className: 'relative bg-bg2 p-3 border border-st2 rounded-md gap-4 flex flex-col',
      defaultNewValue: ((form: { state: { values: FormValues } }) =>
        defaultConditions(form.state.values.conditions?.length || 0)) as never,
      // zodValidation: z.array(z.any()).min(1, 'At least one condition is required'), // A UI issue would occur if we use the zodValidation from the schema,
      removeItemButton: (
        <div className="absolute top-0 right-0 pr-4 pt-5">
          <Tooltip content="Remove condition">
            <Trash2 className="w-4 h-4 text-destructive" />
          </Tooltip>
        </div>
      ),
      addItemButton: (props) => (
        <div className="order-2">
          <Button variant="ghost" className="form-button" {...props}>
            <GitBranch className="w-4 h-4 mr-2" />
            New Branch Condition
          </Button>
        </div>
      ),
      subFields: [
        {
          name: 'alias',
          type: 'sub-item',
          zodValidation: z.string().min(1, 'Condition name is required'),
          component: ({ form, arrayIndex }) => (
            <input
              className="text-p-500 w-full bg-bg2 outline-none focus:border-b focus:border-primary/70"
              type="text"
              value={form.state.values.conditions?.[arrayIndex || 0]?.alias || ''}
              onChange={(e) => {
                form.setFieldValue(`conditions[${arrayIndex || 0}].alias`, e.target.value)
                form.validateField(`conditions[${arrayIndex || 0}].alias`, 'change')
              }}
            />
          ),
        },
        {
          name: 'conditions[0].expression_operators',
          type: 'custom',
          onChangeListenTo: (_, i) => [`conditions[${i || 0}].expressions`],
          className: 'order-2 -mt-4',
          visible: (v, i) => v.conditions?.[i || 0]?.expressions?.length > 1,
          zodValidation: z.array(z.string()).min(0, 'At least one condition is required'),
          component: ({ value, ...props }) => (
            <ToggleGroup
              {...props}
              value={value as string}
              options={[
                { name: 'and', label: 'AND' },
                { name: 'or', label: 'OR' },
              ]}
            />
          ),
          eventDataExtractor: (data) => [data],
          valueFormatter: (value) => value?.[0],
        },
        {
          name: 'conditions[0].expressions',
          type: 'array',
          className: (_, i) =>
            cn(
              'relative bg-bg3 p-2 border border-st2 rounded-md flex flex-col gap-2 pb-6',
              Number(i) > 0 && 'order-2',
              Number(i) > 1 && 'mt-12',
            ),
          zodValidation: z.array(z.any()).min(1, 'At least one expression is required'),
          defaultNewValue: { case_sensitive: false, lhs: '', operation: '', rhs: [] },
          addItemButton: (props) => (
            <div className="order-[100]">
              <Button variant="ghost" className="form-button" {...props} size="sm">
                <CirclePlus className="w-4 h-4 mr-2" />
                Add Expression
              </Button>
            </div>
          ),
          removeItemButton: (
            <div className="absolute bottom-2 right-0 pt-2.5 pr-2.5">
              <Tooltip content="Remove expression">
                <Trash2 className="w-4 h-4 text-tertiary" />
              </Tooltip>
            </div>
          ),
          subFields: [
            {
              name: 'condition_separator',
              type: 'slot',
              element: ({ arrayIndex, baseName, form }) => {
                const value = form.useStore((state) => state.values)
                if (arrayIndex !== undefined && arrayIndex < 2) return null
                return (
                  <div>
                    <div key={arrayIndex} className="absolute top-[-44px]">
                      <div className="absolute top-[-22px] h-[66px] bg-st2 w-[1px] z-0 left-1/2" />
                      <div className="font-jetbrains text-xs-500 uppercase text-secondary bg-bg2 border border-border p-1 rounded px-4 relative z-10">
                        {get(value, `${baseName?.split('.')[0]}.expression_operators[0]`) || ''}
                      </div>
                    </div>
                  </div>
                )
              },
            },
            {
              name: 'conditions[0].expressions[0].lhs',
              componentType: 'input',
              zodValidation: expressionSchema.shape.lhs,
              getDynamicProps: (_, _a, _f, baseName) => ({
                inputProps: {
                  placeholder: 'Value to compare',
                  endIcon: (
                    <NodevarList
                      className="pr-2"
                      nodevars={nodeVars}
                      inputElementSelector={`input[name='${baseName}.lhs']`}
                      sideOffset={460}
                    />
                  ),
                },
              }),
            },
            {
              name: 'conditions[0].expressions[0].operation',
              componentType: 'select',
              zodValidation: expressionSchema.shape.operation,
              inputProps: {
                placeholder: 'Operation',
                options: COMPARISON_OPERATORS,
              },
              afterOnChange: (_v, _i, form, baseName) => {
                form?.setFieldValue(`${baseName}.rhs` as keyof FormValues, [] as never)
                // Resetting rhs re-runs its onChange validator and flashes a
                // "required" error before the user has had a chance to type;
                // clear the field meta so the error only surfaces on save/blur.
                form?.setFieldMeta(`${baseName}.rhs` as keyof FormValues, (meta) => ({
                  ...meta,
                  isTouched: false,
                  errors: [],
                  errorMap: {},
                }))
              },
            },
            {
              name: 'conditions[0].expressions[0].rhs',
              onChangeListenTo: (_v, _i, baseName) => [`${baseName}.operation`] as never,
              visible: (v, _i, baseName) => {
                const operation = get(v, `${baseName}.operation`)
                const operatorInfo = getOperatorInfo(operation)
                return Boolean(operation && operatorInfo?.type !== 'boolean')
              },
              zodValidation: (v, _i, baseName) => {
                const operation = get(v, `${baseName}.operation`)
                const operatorInfo = getOperatorInfo(operation)
                const isFilled = (s: unknown) => typeof s === 'string' && s.trim() !== ''
                if (operation && operatorInfo?.type !== 'boolean') {
                  if (operatorInfo?.rhsType === 'binary')
                    return expressionSchema.shape.rhs.refine(
                      (data) => data?.length === 2 && data.every(isFilled),
                      { message: "'From' and 'To' values are required" },
                    )
                  if (operatorInfo?.type === 'list') return listRhsSchema(operation)
                  return expressionSchema.shape.rhs.refine(
                    (data) => data?.length === 1 && data.every(isFilled),
                    { message: "'Compare with' value is required" },
                  )
                }
                return expressionSchema.shape.rhs
              },
              inputProps: {
                placeholder: 'Compare with',
              },
              eventDataExtractor: (d) => d,
              type: 'custom',
              component: ({ value, form, baseName, name, ...props }) => {
                const operator = get(form.state.values, `${baseName}.operation`)
                const operatorInfo = getOperatorInfo(operator)
                const v = value as string[] | string[][]
                if (operatorInfo?.rhsType === 'binary')
                  return (
                    <div className="flex gap-2">
                      <Input
                        name={`${baseName}.rhs[0]`}
                        value={v?.[0] ?? ''}
                        label="From"
                        onChange={(e) => {
                          const matches = e.target.value.match(/\{\{[^}]+\}\}/g) || []
                          const lastVar = matches[matches.length - 1] || ''
                          props.onChange([matches ? lastVar : e.target.value])
                        }}
                        endIcon={
                          <NodevarList
                            className="pr-2"
                            nodevars={nodeVars}
                            inputElementSelector={`input[name='${baseName}.rhs[0]']`}
                          />
                        }
                      />
                      <Input
                        name={`${baseName}.rhs[1]`}
                        value={v?.[1] ?? ''}
                        label="To"
                        onChange={(e) => {
                          const matches = e.target.value.match(/\{\{[^}]+\}\}/g) || []
                          const lastVar = matches[matches.length - 1] || ''
                          props.onChange([v?.[0], matches ? lastVar : e.target.value])
                        }}
                        endIcon={
                          <NodevarList
                            className="pr-2"
                            nodevars={nodeVars}
                            inputElementSelector={`input[name='${baseName}.rhs[1]']`}
                          />
                        }
                      />
                    </div>
                  )
                if (operatorInfo?.type === 'list')
                  return (
                    <BadgeInput
                      value={(v?.[0] as string[]) || []}
                      onChange={(listValue: string[]) => {
                        props.onChange([listValue])
                      }}
                      name={name}
                      endIcon={
                        <NodevarList
                          className="pr-2"
                          nodevars={nodeVars}
                          inputElementSelector={`input[name='${baseName}.rhs']`}
                        />
                      }
                    />
                  )
                return (
                  <div>
                    <Input
                      name={name}
                      value={v?.[0] ?? ''}
                      placeholder={
                        operatorInfo?.type === 'list'
                          ? 'Use comma to separate multiple values'
                          : 'Compare with'
                      }
                      onChange={(e) => props.onChange([e.target.value])}
                      endIcon={
                        <NodevarList
                          className="pr-2"
                          nodevars={nodeVars}
                          inputElementSelector={`input[name='${baseName}.rhs']`}
                        />
                      }
                    />
                  </div>
                )
              },
            },
            {
              name: 'conditions[0].expressions[0].case_sensitive',
              componentType: 'checkbox',
              zodValidation: expressionSchema.shape.case_sensitive,
              onChangeListenTo: ['conditions[0].expressions[0].operation'],
              visible: (v, _i, baseName) => {
                const operation = get(v, `${baseName}.operation`)
                const operatorInfo = getOperatorInfo(operation)
                return operatorInfo?.enableCaseSensitive || false
              },
              className: 'mt-1',
              inputProps: {
                label: 'Case sensitive comparision',
                defaultChecked: false,
              },
            },
          ],
        },
      ],
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const BranchV2SidebarComponent = (props: SidebarComponentProps<BranchV2NodeData>) => {
  const { nodeVars } = props
  const formFields = fields(nodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
